<template>
  <div class="profile" v-if="profile">
    <div class="profile-bg"></div>
    <div class="profile-content">
      <!-- User: Name, Email -->
      <div class="profile__fullName">
        <h1 class="user__name">{{ profile.first_name }} {{ profile.last_name }}</h1>
        <p class="user__email">{{ profile.email }}</p>
      </div>

      <!-- user Details: Phone Number, Address -->
      <div class="profile__details" :class="{ singleItem: isTechnician }">
        <div class="profile__details-contentbox">
          <!-- User Contacts: Phone, Address -->
          <div class="profile__contacts">
            <content-box>
              <span slot="label"><img src="../../assets/img/ico-phone.svg"/></span>
              <span slot="content">{{ profile.phone_number || 'Update profile' }} </span>
            </content-box>
            <content-box>
              <span slot="label"><img src="../../assets/img/ico-house.svg"/></span>
              <span slot="content">
                {{ profile.street }}
                {{ profile.postal_code }}
                {{ profile.city || 'Update profile' }}
              </span>
            </content-box>
          </div>
        </div>
      </div>

      <!-- Profile Buttons: Edit, Change Password, Logout -->
      <div class="profile__buttons">
        <button class="profile__buttons-btn" @click="editMode = true">
          {{ $t('Web.Profile.Buttons.Edit') }}
        </button>

        <router-link tag="button" to="/changepassword" class="profile__buttons-btn">
          {{ $t('Web.ChangePassword.Title') }}</router-link
        >

        <cactus-sign-out class="profile__buttons-btn">
          <span slot="body">{{ $t('Web.Profile.Buttons.Logout') }}</span>
        </cactus-sign-out>
      </div>

      <!-- Service Box -->
      <div class="profile__details singleItem">
        <div class="profile__details-contentbox" v-if="profile">
          <h3 class="profile__details-title">{{ $t('Web.Profile.Service') }}</h3>
          <service-person />
        </div>
      </div>

      <!-- Other Links: What's New, Imprint, Terms, Privacy, OSS -->
      <div class="profile__legal">
        <span>{{ $t('Web.Profile.Copyright') }} - {{ currentYear }}</span>
        <div class="profile__legal-links">
          <router-link to="/news">{{ $t('Web.Profile.Buttons.News') }}</router-link>
          <router-link to="/imprint">{{ $t('Web.Profile.Buttons.Imprint') }}</router-link>
          <router-link to="/termsandconditions">{{
            $t('Web.Profile.Buttons.Conditions')
          }}</router-link>
          <router-link to="/privacyagreement">{{ $t('Web.Profile.Buttons.Privacy') }}</router-link>
          <router-link to="/oss">{{ $t('Web.Profile.Buttons.OpenSource') }}</router-link>
        </div>
      </div>
    </div>

    <!-- Modal: Edit Profile -->
    <modal v-if="editMode" @close="editMode = false" @profileWasSaved="profileSaved">
      <div slot="header" class="editProfile__header">
        <h1>{{ $t('Web.Profile.Buttons.Edit') }}</h1>
      </div>
      <div slot="body">
        <editProfile />
      </div>
      <div slot="footer"></div>
    </modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import ContentBox from '../_common/contentBox';
import EditProfile from '../_common/editProfile';
import Modal from '../_common/modal';
import ServicePerson from '../_common/servicePerson';

export default {
  name: 'profile',
  components: {
    ContentBox,
    EditProfile,
    Modal,
    ServicePerson
  },
  data() {
    return {
      editMode: false,
      currentYear: this.$moment(new Date()).year()
    };
  },

  methods: {
    ...mapActions(['getProfile', 'updateProfile']),

    profileUpdate() {
      this.$showGlobalLoadingSpinner();
      this.profile.allow_data_usage = !this.profile.allow_data_usage;
      this.updateProfile(this.profile).finally(() => {
        this.$hideGlobalLoadingSpinner();
      });
    },

    profileSaved() {
      this.editMode = false;
      this.getProfile();
    }
  },

  computed: {
    ...mapGetters(['profile', 'isAuthenticated', 'isTechnician'])
  }
};
</script>

<style lang="scss" scoped>
.profile {
  padding: 120px 0 60px 0;

  &-bg {
    background: $charcoal-grey url('../../assets/img/img-battery-big-no-bg.svg') no-repeat center
      56px;
    position: fixed;
    height: 160px;
    top: 0;
    width: 100%;
    z-index: 1;
  }

  &-content {
    position: relative;
    z-index: 0;
    max-width: $maxWidth * 2;
    background: #fff;
    height: 100%;
    margin: 0 auto;
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;

    @include breakpoint(xs) {
      max-width: $maxWidth;
    }
  }

  &__fullName {
    flex: 0 0 100%;
    text-align: center;

    h1 {
      @include type-headline-variant1($greeny-blue);
    }

    p {
      margin-top: 1.25rem;
    }
  }

  &__contacts {
    display: flex;

    .box {
      display: flex;
      flex: 1;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      flex-direction: column;
      padding-bottom: 24px;
      height: auto;
      text-align: center;
      border-right: 0;
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;

      & + .box {
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        border-right: 1px;
        border-top-right-radius: 8px;
        border-bottom-right-radius: 8px;
        border-color: #becdd7;
        border-style: solid;
      }

      /deep/ .content {
        font-size: 12px;
        line-height: 16px;
        text-align: center;
        color: #879baa;
        padding: 0;
      }
    }
  }

  .singleItem {
    justify-content: center;
    align-items: center;
  }

  .marketing {
    margin-top: 24px;
  }

  &__details {
    width: 100%;
    // margin: 24px 0 10px 0;
    margin: 48px 0;
    flex-direction: column;
    align-items: center;

    @include breakpoint(sm) {
      display: flex;
      justify-content: space-between;
    }

    &-title {
      display: block;
      @include textSmall;
      margin: 0 0 10px 8px;
      color: $greeny-blue;
      font-weight: normal;
    }

    &-contentbox {
      max-width: $maxWidth;
      width: 100%;

      @include breakpoint(sm) {
        flex: 0 0 48%;
        // flex: 1;
      }
    }
  }

  &__buttons {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    &-btn {
      width: 90%;
      height: 48px;
      margin: 8px 0;

      @include breakpoint(sm) {
        width: 42%;
      }

      button {
        height: 48px;
        padding: 0;
      }
    }
  }

  &__legal {
    width: 100%;
    text-align: center;

    span {
      font-size: 13px;
      color: #3c464b;
    }

    &-links a {
      display: block;
      font-size: 14px;
      line-height: 1.14;
      max-width: 280px;
      margin: 12px auto 0;
    }
  }
}
</style>
